import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import AdminPage from "../AdminPage";
import { apiClient } from "../../utils/apiClient";
import { useSnackbar } from "../../components/SnackbarProvider";
import { useDispatch } from "react-redux";
import { openEmailModal } from "../../store/adminSlice";
import { TinyValueComponent } from "../../internal/components/TinyValueComponent";

interface EmailInfo {
  id: string;
  subject: string | null;
  fromEmail: string | null;
  date: string;
}

interface EmailTestSetDetail {
  id: string;
  name: string;
  description: string | null;
  emailIds: string[];
  emails: EmailInfo[];
  createdAt: string;
  updatedAt: string;
}

export function EmailTestSetDetailPage() {
  const { testSetId } = useParams<{ testSetId: string }>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [testSet, setTestSet] = useState<EmailTestSetDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add emails dialog
  const [addEmailsDialogOpen, setAddEmailsDialogOpen] = useState(false);
  const [emailIdsToAdd, setEmailIdsToAdd] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (testSetId) {
      fetchTestSet();
    }
  }, [testSetId]);

  const fetchTestSet = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ testSet: EmailTestSetDetail }>(
        `/admin/email-test-sets/get`,
        { id: testSetId }
      );
      setTestSet(res.testSet);
    } catch (error) {
      console.error("Error fetching test set:", error);
      setError("Failed to load test set");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmails = async () => {
    if (!testSetId) return;

    const emailIds = emailIdsToAdd
      .split(/[,\s]+/)
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (emailIds.length === 0) {
      showSnackbar("Please enter at least one email ID", 3000, "info");
      return;
    }

    setAdding(true);
    try {
      const res = await apiClient.post<{
        success: boolean;
        added: number;
        alreadyInSet: number;
        notFound: string[];
      }>(`/admin/email-test-sets/add-emails`, {
        testSetId,
        emailIds,
      });

      let message = `Added ${res.added} email(s)`;
      if (res.alreadyInSet > 0) {
        message += `, ${res.alreadyInSet} already in set`;
      }
      if (res.notFound.length > 0) {
        message += `, ${res.notFound.length} not found`;
      }

      showSnackbar(message, 4000, "success");
      setAddEmailsDialogOpen(false);
      setEmailIdsToAdd("");
      fetchTestSet();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to add emails";
      showSnackbar(errorMessage, 4000, "error");
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveEmail = async (emailId: string) => {
    if (!testSet) return;

    try {
      await apiClient.post(`/admin/email-test-sets/remove-email`, {
        testSetId: testSet.id,
        emailId,
      });

      setTestSet({
        ...testSet,
        emails: testSet.emails.filter((e) => e.id !== emailId),
        emailIds: testSet.emailIds.filter((id) => id !== emailId),
      });

      showSnackbar("Email removed from test set", 2000, "success");
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || "Failed to remove email";
      showSnackbar(errorMessage, 3000, "error");
    }
  };

  const handleOpenEmail = (emailId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    dispatch(openEmailModal(emailId));
  };

  if (loading) {
    return (
      <AdminPage title="Test Set Emails">
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </AdminPage>
    );
  }

  if (error || !testSet) {
    return (
      <AdminPage title="Test Set Emails">
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Test set not found"}
          </Alert>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate("/admin/email-test-sets")}
          >
            Back to Test Sets
          </Button>
        </Box>
      </AdminPage>
    );
  }

  return (
    <AdminPage title={testSet.name}>
      <Box sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box>
            <Button
              startIcon={<BackIcon />}
              onClick={() => navigate("/admin/email-test-sets")}
              sx={{ mb: 1 }}
            >
              Back to Test Sets
            </Button>
            <Typography variant="h5">{testSet.name}</Typography>
            {testSet.description && (
              <Typography variant="body2" color="text.secondary">
                {testSet.description}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddEmailsDialogOpen(true)}
          >
            Add Emails
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          {testSet.emails.length} email(s) in this test set
        </Typography>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email ID</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>From</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testSet.emails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                      No emails in this test set. Click "Add Emails" to add
                      some.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                testSet.emails.map((email) => (
                  <TableRow key={email.id} hover>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenEmail(email.id, e)}
                          title="View email"
                          sx={{ p: 0.25 }}
                        >
                          <EmailIcon fontSize="small" color="primary" />
                        </IconButton>
                        <TinyValueComponent value={email.id} clipboard cropText />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {email.subject || "(No subject)"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {email.fromEmail || "Unknown"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(email.date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from test set">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveEmail(email.id)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Emails Dialog */}
        <Dialog
          open={addEmailsDialogOpen}
          onClose={() => !adding && setAddEmailsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Emails to Test Set</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter email IDs separated by commas or newlines.
            </Typography>
            <TextField
              fullWidth
              label="Email IDs"
              value={emailIdsToAdd}
              onChange={(e) => setEmailIdsToAdd(e.target.value)}
              multiline
              rows={4}
              placeholder="Enter email IDs..."
              autoFocus
              disabled={adding}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddEmailsDialogOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAddEmails}
              disabled={!emailIdsToAdd.trim() || adding}
            >
              {adding ? <CircularProgress size={20} /> : "Add Emails"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminPage>
  );
}
